import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "../services/api";
import SalesPerformanceChart from "../components/SalesPerformanceChart";
import {
  StoreIcon,
  ShoppingBagIcon,
  CreditCardIcon,
  PlusIcon,
  AlertTriangleIcon,
  ExternalLinkIcon,
  CheckBadgeIcon,
  LockIcon,
  LogOutIcon
} from "../components/Icons";

function Dashboard({ user, onLogout }) {
  const [data, setData] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const inrFormatter = useMemo(
    () =>
      new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 0,
      }),
    []
  );

  const [fulfillmentOverview, setFulfillmentOverview] = useState({
    pendingPickupRequests: 0,
    readyForPickup: 0,
    vendorDeliveries: 0,
    activePickupLocations: 1
  });

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const [dashRes, readinessRes, fulRes] = await Promise.all([
        api.get("/vendor/dashboard"),
        api.get("/vendor/verification/status").catch(() => ({ data: null })),
        api.get("/vendor/fulfillment/overview").catch(() => ({ data: null }))
      ]);
      setData(dashRes.data);
      setReadiness(readinessRes.data || null);
      if (fulRes?.data?.overview) {
        setFulfillmentOverview(fulRes.data.overview);
      }
    } catch (err) {
      setError(err.response?.data?.msg || "Failed to load seller dashboard.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, []);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";

  if (loading) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div className="skeleton" style={{ height: "28px", width: "260px", marginBottom: "8px" }} />
            <div className="skeleton" style={{ height: "14px", width: "320px" }} />
          </div>
          <div className="skeleton" style={{ height: "38px", width: "130px", borderRadius: "8px" }} />
        </div>

        <div className="skeleton" style={{ height: "84px", borderRadius: "14px" }} />

        <div className="row g-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="col-md-3 col-6">
              <div className="skeleton" style={{ height: "100px", borderRadius: "12px" }} />
            </div>
          ))}
        </div>

        <div className="row g-4">
          <div className="col-lg-8"><div className="skeleton" style={{ height: "320px", borderRadius: "14px" }} /></div>
          <div className="col-lg-4"><div className="skeleton" style={{ height: "320px", borderRadius: "14px" }} /></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ background: "#fee2e2", border: "1px solid #fecaca", borderRadius: "10px", padding: "16px 20px", color: "#dc2626" }}>
        <strong>Error loading dashboard:</strong> {error}
      </div>
    );
  }

  const {
    store,
    currentPlan = { name: "Basic", price: 0, productLimit: 5, commissionRate: 10 },
    productCount = 0,
    productLimit = 5,
    kpis = {
      grossSales: 0,
      todaySales: 0,
      totalOrders: 0,
      totalUnitsSold: 0,
      totalCommission: 0,
      sellerEarnings: 0,
      averageRating: 0,
      totalReviews: 0,
      salesGrowthPct: null
    },
    channelBreakdown = {},
    omnichannelSummary = {},
    customerGeographicInflow = {},
    salesChartData = [],
    orderActivity = { new: 0, processing: 0, shipped: 0, delivered: 0, returns: 0 },
    storeHealth = {
      profileCompletion: 60,
      hasStoreImages: true,
      hasBusinessDetails: true,
      bankDetailsStatus: "verified",
      documentsStatus: "verified",
      reviewsCount: 0,
      averageRating: 0
    },
    bestSeller = null,
    topProducts = [],
    recentOrders = [],
    attentionItems = [],
    profileCompletion = 60
  } = data || {};

  const storeName = store?.name || `${user.firstName || "Seller"}'s Store`;
  const remainingSlots = Math.max(0, productLimit - productCount);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "22px" }}>
      {/* ═══════════════════════ 1. HEADER & PRIMARY CTA ═══════════════════════ */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "14px",
          paddingBottom: "16px",
          borderBottom: "1px solid #edf0f4"
        }}
      >
        <div>
          <h1 style={{ fontSize: "22px", fontWeight: "900", color: "#111315", margin: "0 0 2px" }}>
            {greeting}, {user.firstName || "Seller"}! 👋
          </h1>
          <p style={{ fontSize: "13.5px", color: "var(--text-muted)", margin: 0 }}>
            Here&apos;s how your store is performing today.
          </p>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
          <Link
            to="/vendor/products/new"
            className="btn btn-primary"
            style={{ padding: "9px 20px", fontSize: "13px", fontWeight: "800", gap: "6px" }}
          >
            <PlusIcon size={16} />
            <span>Add Product</span>
          </Link>
          {onLogout && (
            <button
              type="button"
              onClick={onLogout}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                padding: "9px 16px",
                fontSize: "13px",
                fontWeight: "750",
                color: "#dc2626",
                background: "#fef2f2",
                border: "1.5px solid #fecaca",
                borderRadius: "10px",
                cursor: "pointer",
                transition: "all 0.15s ease"
              }}
              title="Logout from Seller Panel"
            >
              <LogOutIcon size={16} color="#dc2626" />
              <span>Logout</span>
            </button>
          )}
        </div>
      </div>

      {/* ═══════════════════════ MANDATORY SELLER VERIFICATION ALERT ═══════════════════════ */}
      {readiness && !readiness.isEligible && (
        <div
          style={{
            background: "#eff6ff",
            border: "1.5px solid #bfdbfe",
            borderRadius: "14px",
            padding: "18px 22px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "16px",
            boxShadow: "0 4px 12px rgba(37,99,235,0.06)"
          }}
        >
          <div style={{ display: "flex", alignItems: "flex-start", gap: "14px", maxWidth: "620px" }}>
            <div style={{ width: "42px", height: "42px", borderRadius: "10px", background: "rgba(29, 78, 216, 0.1)", display: "grid", placeItems: "center", flexShrink: 0 }}>
              <LockIcon size={22} color="#1d4ed8" />
            </div>
            <div>
              <span style={{ fontSize: "11px", fontWeight: "800", color: "#1d4ed8", textTransform: "uppercase" }}>
                SELLER ONBOARDING VERIFICATION
              </span>
              <h3 style={{ fontSize: "16px", fontWeight: "900", color: "#1e3a8a", margin: "2px 0 4px" }}>
                Your Seller Account is Approved
              </h3>
              <p style={{ fontSize: "13px", color: "#3b82f6", margin: 0, lineHeight: "1.4" }}>
                Complete your official identity (Aadhaar &amp; PAN) and bank payout verification to enable product creation and start selling on the marketplace.
              </p>
              <div style={{ display: "flex", gap: "12px", marginTop: "10px", fontSize: "12px", color: "#1e40af", fontWeight: "700" }}>
                <span>Documents: {readiness.requirements?.documentsVerified ? "✓ Verified" : "⏳ Pending"}</span>
                <span>&bull;</span>
                <span>Bank: {readiness.requirements?.bankVerified ? "✓ Verified" : "⏳ Pending"}</span>
                <span>&bull;</span>
                <span>Listing: Locked</span>
              </div>
            </div>
          </div>

          <Link
            to="/vendor/verification"
            className="btn btn-primary"
            style={{ fontSize: "13px", padding: "9px 20px", fontWeight: "800", background: "#1d4ed8", borderColor: "#1d4ed8" }}
          >
            Complete Verification &rarr;
          </Link>
        </div>
      )}

      {/* ═══════════════════════ 2. COMPACT STORE IDENTITY SUMMARY ═══════════════════════ */}
      <div
        style={{
          background: "#ffffff",
          border: "1px solid #e0e4ea",
          borderRadius: "14px",
          padding: "16px 20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "16px",
          flexWrap: "wrap",
          boxShadow: "0 2px 8px rgba(16,20,28,0.02)"
        }}
      >
        {/* Store Logo & Details */}
        <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
          <div
            style={{
              width: "48px",
              height: "48px",
              borderRadius: "10px",
              background: "#faedea",
              color: "var(--primary)",
              display: "grid",
              placeItems: "center",
              fontSize: "20px",
              fontWeight: "900",
              border: "1.5px solid #f9dede",
              overflow: "hidden",
              flexShrink: 0
            }}
          >
            {store?.logo?.imageurl ? (
              <img src={store.logo.imageurl} alt={storeName} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            ) : (
              storeName.charAt(0).toUpperCase()
            )}
          </div>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
              <strong style={{ fontSize: "17px", fontWeight: "900", color: "#111315" }}>{storeName}</strong>
              {(store?.isVerified || store?.isTrusted) && (
                <span title="Verified Trusted Seller" style={{ color: "#2563eb", display: "inline-flex", alignItems: "center" }}>
                  <CheckBadgeIcon size={18} />
                </span>
              )}
              {(() => {
                const now = new Date();
                const utc = now.getTime() + now.getTimezoneOffset() * 60000;
                const ist = new Date(utc + 5.5 * 3600000);
                const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
                const currentDay = days[ist.getDay()];
                const currentMinutes = ist.getHours() * 60 + ist.getMinutes();
                const [oh = 10, om = 0] = (store?.openTime || "10:00").split(':').map(Number);
                const [ch = 20, cm = 0] = (store?.closeTime || "20:00").split(':').map(Number);
                const openMin = oh * 60 + om;
                const closeMin = ch * 60 + cm;
                const operatingDays = Array.isArray(store?.operatingDays) && store.operatingDays.length > 0 ? store.operatingDays : ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
                const isDayOpen = operatingDays.includes(currentDay);
                const isTimeOpen = currentMinutes >= openMin && currentMinutes < closeMin;
                const isLiveOpen = !store?.isManualClosed && isDayOpen && isTimeOpen;

                if (isLiveOpen) {
                  return (
                    <span style={{ background: "#dcfce7", color: "#166534", fontSize: "10.5px", fontWeight: "800", padding: "2px 8px", borderRadius: "99px", display: "inline-flex", alignItems: "center", gap: "5px" }}>
                      <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: "#16a34a" }} />
                      Open Now
                    </span>
                  );
                } else {
                  return (
                    <span style={{ background: "#fee2e2", color: "#991b1b", fontSize: "10.5px", fontWeight: "800", padding: "2px 8px", borderRadius: "99px", display: "inline-flex", alignItems: "center", gap: "5px" }} title="Outside operating hours">
                      <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: "#dc2626" }} />
                      Closed Now
                    </span>
                  );
                }
              })()}
              <span style={{ background: "#f1f5f9", color: "#475569", fontSize: "10.5px", fontWeight: "700", padding: "2px 7px", borderRadius: "99px" }}>
                Active Profile
              </span>
            </div>
            <div style={{ fontSize: "12.5px", color: "var(--text-muted)", marginTop: "2px" }}>
              {store?.category || "Fashion & Lifestyle"} &middot; {store?.address?.city ? `${store.address.city}, India` : "India"}
            </div>
          </div>
        </div>

        {/* Quick Action Links */}
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          {store?.slug && (
            <a
              href={`https://www.imonline.shop/store/${store.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-outline-secondary"
              style={{ padding: "7px 14px", fontSize: "12.5px", fontWeight: "700", gap: "4px" }}
            >
              <span>View Store</span>
              <ExternalLinkIcon size={14} />
            </a>
          )}
          <Link
            to="/vendor/store"
            className="btn btn-outline-secondary"
            style={{ padding: "7px 14px", fontSize: "12.5px", fontWeight: "700" }}
          >
            Edit Store
          </Link>
        </div>
      </div>

      {/* ═══════════════════════ 3. PRIMARY BUSINESS KPIS (NO REPETITION) ═══════════════════════ */}
      <div className="row g-3">
        {/* KPI 1: Today's Sales */}
        <div className="col-lg-3 col-6">
          <div
            style={{
              background: "#ffffff",
              border: "1px solid #e0e4ea",
              borderRadius: "12px",
              padding: "16px 18px",
              boxShadow: "0 2px 6px rgba(0,0,0,0.02)"
            }}
          >
            <div style={{ fontSize: "11px", fontWeight: "800", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.6px" }}>
              TODAY&apos;S SALES
            </div>
            <div style={{ fontSize: "24px", fontWeight: "900", color: "var(--primary)", margin: "4px 0 2px" }}>
              {inrFormatter.format(kpis.todaySales)}
            </div>
            <div style={{ fontSize: "11.5px", color: "var(--text-muted)" }}>
              {inrFormatter.format(kpis.grossSales)} lifetime sales
            </div>
          </div>
        </div>

        {/* KPI 2: Total Orders */}
        <div className="col-lg-3 col-6">
          <div
            style={{
              background: "#ffffff",
              border: "1px solid #e0e4ea",
              borderRadius: "12px",
              padding: "16px 18px",
              boxShadow: "0 2px 6px rgba(0,0,0,0.02)"
            }}
          >
            <div style={{ fontSize: "11px", fontWeight: "800", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.6px" }}>
              ORDERS
            </div>
            <div style={{ fontSize: "24px", fontWeight: "900", color: "#111315", margin: "4px 0 2px" }}>
              {kpis.totalOrders}
            </div>
            <div style={{ fontSize: "11.5px", color: "var(--text-muted)" }}>
              {kpis.totalUnitsSold} units sold this month
            </div>
          </div>
        </div>

        {/* KPI 3: Products Listed */}
        <div className="col-lg-3 col-6">
          <div
            style={{
              background: "#ffffff",
              border: "1px solid #e0e4ea",
              borderRadius: "12px",
              padding: "16px 18px",
              boxShadow: "0 2px 6px rgba(0,0,0,0.02)"
            }}
          >
            <div style={{ fontSize: "11px", fontWeight: "800", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.6px" }}>
              PRODUCTS
            </div>
            <div style={{ fontSize: "24px", fontWeight: "900", color: "#111315", margin: "4px 0 2px" }}>
              {productCount} <span style={{ fontSize: "14px", color: "var(--text-muted)", fontWeight: "700" }}>/ {productLimit}</span>
            </div>
            <div style={{ fontSize: "11.5px", color: remainingSlots <= 1 ? "#dc2626" : "var(--text-muted)", fontWeight: remainingSlots <= 1 ? "700" : "500" }}>
              {remainingSlots} {remainingSlots === 1 ? "slot" : "slots"} remaining
            </div>
          </div>
        </div>

        {/* KPI 4: Net Earnings */}
        <div className="col-lg-3 col-6">
          <div
            style={{
              background: "#ffffff",
              border: "1px solid #e0e4ea",
              borderRadius: "12px",
              padding: "16px 18px",
              boxShadow: "0 2px 6px rgba(0,0,0,0.02)"
            }}
          >
            <div style={{ fontSize: "11px", fontWeight: "800", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.6px" }}>
              NET EARNINGS
            </div>
            <div style={{ fontSize: "24px", fontWeight: "900", color: "#16a34a", margin: "4px 0 2px" }}>
              {inrFormatter.format(kpis.sellerEarnings)}
            </div>
            <div style={{ fontSize: "11.5px", color: "var(--text-muted)" }}>
              After {currentPlan.commissionRate}% platform commission
            </div>
          </div>
        </div>
      </div>

      {/* ═══════════════════════ 4. SALES PERFORMANCE & STORE HEALTH ═══════════════════════ */}
      <div className="row g-4">
        {/* Left: Sales Performance Interactive Chart */}
        <div className="col-lg-8">
          <SalesPerformanceChart
            chartData={salesChartData}
            kpis={kpis}
            inrFormatter={inrFormatter}
          />
        </div>

        {/* Right: Actionable Store Health Panel */}
        <div className="col-lg-4">
          <div
            style={{
              background: "#ffffff",
              border: "1px solid #e0e4ea",
              borderRadius: "14px",
              padding: "22px 24px",
              boxShadow: "0 4px 14px rgba(16,20,28,0.03)",
              display: "flex",
              flexDirection: "column",
              height: "100%"
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
              <div>
                <span style={{ fontSize: "11px", fontWeight: "800", color: "var(--primary)", textTransform: "uppercase", letterSpacing: "0.8px" }}>
                  ACCOUNT READINESS
                </span>
                <h3 style={{ fontSize: "18px", fontWeight: "900", color: "var(--dark)", margin: "2px 0 0" }}>
                  Store Health
                </h3>
              </div>
              <span style={{ fontSize: "14px", fontWeight: "900", color: profileCompletion >= 80 ? "#16a34a" : "var(--primary)" }}>
                {profileCompletion}%
              </span>
            </div>

            {/* Progress Bar */}
            <div style={{ background: "#eef1f5", borderRadius: "99px", height: "6px", marginBottom: "18px" }}>
              <div
                style={{
                  width: `${profileCompletion}%`,
                  height: "100%",
                  background: profileCompletion >= 80 ? "#16a34a" : "var(--primary)",
                  borderRadius: "99px",
                  transition: "width 0.4s ease"
                }}
              />
            </div>

            {/* Structured Health Checklist */}
            <div style={{ display: "flex", flexDirection: "column", gap: "11px", flex: 1 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "13px" }}>
                <span style={{ color: "var(--text-muted)" }}>Profile Completion</span>
                <strong style={{ color: profileCompletion >= 80 ? "#16a34a" : "var(--dark)" }}>{profileCompletion}%</strong>
              </div>

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "13px" }}>
                <span style={{ color: "var(--text-muted)" }}>Products Listed</span>
                <strong style={{ color: "#111315" }}>{productCount} / {productLimit}</strong>
              </div>

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "13px" }}>
                <span style={{ color: "var(--text-muted)" }}>Store Images</span>
                <span style={{ color: storeHealth.hasStoreImages ? "#16a34a" : "#d97706", fontWeight: "800", fontSize: "12px" }}>
                  {storeHealth.hasStoreImages ? "✓ Complete" : "⚠ Incomplete"}
                </span>
              </div>

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "13px" }}>
                <span style={{ color: "var(--text-muted)" }}>Business Details</span>
                <span style={{ color: storeHealth.hasBusinessDetails ? "#16a34a" : "#d97706", fontWeight: "800", fontSize: "12px" }}>
                  {storeHealth.hasBusinessDetails ? "✓ Complete" : "⚠ Action required"}
                </span>
              </div>

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "13px" }}>
                <span style={{ color: "var(--text-muted)" }}>Bank Details</span>
                <span style={{ color: storeHealth.bankDetailsStatus === "verified" ? "#16a34a" : "#dc2626", fontWeight: "800", fontSize: "12px" }}>
                  {storeHealth.bankDetailsStatus === "verified" ? "✓ Verified" : "⚠ Action required"}
                </span>
              </div>

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "13px" }}>
                <span style={{ color: "var(--text-muted)" }}>Documents</span>
                <span style={{ color: storeHealth.documentsStatus === "verified" ? "#16a34a" : "#d97706", fontWeight: "800", fontSize: "12px" }}>
                  {storeHealth.documentsStatus === "verified" ? "✓ Verified" : "In Review"}
                </span>
              </div>

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "13px" }}>
                <span style={{ color: "var(--text-muted)" }}>Store Rating</span>
                <strong style={{ color: "#d97706" }}>
                  {kpis.averageRating > 0 ? `★ ${kpis.averageRating} (${kpis.totalReviews})` : "New Store"}
                </strong>
              </div>
            </div>

            <div style={{ marginTop: "16px", paddingTop: "14px", borderTop: "1px solid #edf0f4" }}>
              <Link
                to="/vendor/store"
                className="btn btn-outline-secondary w-100"
                style={{ fontSize: "12.5px", padding: "8px", fontWeight: "800" }}
              >
                Complete Store Profile &rarr;
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* ═══════════════════════ 4.2. FULFILLMENT & PICKUP MANAGEMENT BANNER ═══════════════════════ */}
      <div
        style={{
          background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
          border: "1px solid #e0e4ea",
          borderRadius: "14px",
          padding: "20px 24px",
          boxShadow: "0 4px 14px rgba(16,20,28,0.03)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "16px"
        }}
      >
        <div>
          <span style={{ fontSize: "11px", fontWeight: "800", color: "var(--primary)", textTransform: "uppercase", letterSpacing: "0.8px" }}>
            FULFILLMENT &amp; LOGISTICS
          </span>
          <h3 style={{ fontSize: "18px", fontWeight: "900", color: "#0f172a", margin: "2px 0 6px" }}>
            Order Pickup &amp; Delivery Management 🚚
          </h3>
          <div style={{ display: "flex", gap: "16px", flexWrap: "wrap", fontSize: "13px", color: "#475569" }}>
            <span>⏳ Pending Pickup Requests: <strong style={{ color: "#b45309" }}>{fulfillmentOverview.pendingPickupRequests || 0}</strong></span>
            <span>&bull;</span>
            <span>🏪 Ready for Customer: <strong style={{ color: "#16a34a" }}>{fulfillmentOverview.readyForPickup || 0}</strong></span>
            <span>&bull;</span>
            <span>🛵 Vendor Deliveries: <strong style={{ color: "#2563eb" }}>{fulfillmentOverview.vendorDeliveries || 0}</strong></span>
            <span>&bull;</span>
            <span>📍 Active Pickup Hubs: <strong style={{ color: "#7e22ce" }}>{fulfillmentOverview.activePickupLocations || 1}</strong></span>
          </div>
        </div>

        <Link
          to="/vendor/pickup-requests"
          className="btn btn-primary"
          style={{ padding: "9px 20px", fontSize: "13px", fontWeight: "800" }}
        >
          Manage Fulfillment &rarr;
        </Link>
      </div>

      {/* ═══════════════════════ 4.5. OMNICHANNEL CHANNELS & CUSTOMER INFLOW ═══════════════════════ */}
      <div className="row g-4">
        {/* Omnichannel Channel Mix */}
        <div className="col-lg-12">
          <div
            style={{
              background: "#ffffff",
              border: "1px solid #e0e4ea",
              borderRadius: "14px",
              padding: "22px 24px",
              boxShadow: "0 4px 14px rgba(16,20,28,0.03)"
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px", flexWrap: "wrap", gap: "10px" }}>
              <div>
                <span style={{ fontSize: "11px", fontWeight: "800", color: "#2563eb", textTransform: "uppercase", letterSpacing: "0.8px" }}>
                  OMNICHANNEL DISTRIBUTION
                </span>
                <h3 style={{ fontSize: "18px", fontWeight: "900", color: "var(--dark)", margin: "2px 0 0" }}>
                  Order &amp; Customer Interaction Channels
                </h3>
              </div>
              <div style={{ display: "flex", gap: "8px" }}>
                <span style={{ fontSize: "12px", background: "#f0fdf4", color: "#166534", padding: "4px 10px", borderRadius: "99px", fontWeight: "700", border: "1px solid #bbf7d0" }}>
                  Online Deliveries: {omnichannelSummary.onlineOrders || 0}
                </span>
                <span style={{ fontSize: "12px", background: "#eff6ff", color: "#1e40af", padding: "4px 10px", borderRadius: "99px", fontWeight: "700", border: "1px solid #bfdbfe" }}>
                  In-Store / Pickups: {omnichannelSummary.offlineInStoreTotal || 0}
                </span>
                <span style={{ fontSize: "12px", background: "#fef3c7", color: "#92400e", padding: "4px 10px", borderRadius: "99px", fontWeight: "700", border: "1px solid #fde68a" }}>
                  Phone Leads: {omnichannelSummary.callLeads || 0}
                </span>
              </div>
            </div>

            {/* 5 Channel Cards Grid */}
            <div className="row g-3">
              {/* 1. Home Delivery */}
              <div className="col-md">
                <div style={{ background: "#f8fafc", border: "1.5px solid #edf0f4", borderRadius: "12px", padding: "14px 16px", height: "100%", position: "relative" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "8px" }}>
                    <span style={{ fontSize: "20px" }}>🚚</span>
                    <span style={{ fontSize: "10px", fontWeight: "800", background: "#dcfce7", color: "#15803d", padding: "2px 6px", borderRadius: "4px" }}>ONLINE</span>
                  </div>
                  <div style={{ fontSize: "13px", fontWeight: "800", color: "#1e293b" }}>Home Delivery</div>
                  <div style={{ fontSize: "18px", fontWeight: "900", color: "#0f172a", marginTop: "4px" }}>
                    {channelBreakdown.HOME_DELIVERY?.count || 0} <span style={{ fontSize: "12px", fontWeight: "600", color: "#64748b" }}>orders</span>
                  </div>
                  <div style={{ fontSize: "12px", color: "#64748b", marginTop: "2px", fontWeight: "600" }}>
                    {inrFormatter.format(channelBreakdown.HOME_DELIVERY?.revenue || 0)}
                  </div>
                </div>
              </div>

              {/* 2. Pick Up From Shop */}
              <div className="col-md">
                <div style={{ background: "#f8fafc", border: "1.5px solid #edf0f4", borderRadius: "12px", padding: "14px 16px", height: "100%" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "8px" }}>
                    <span style={{ fontSize: "20px" }}>🏪</span>
                    <span style={{ fontSize: "10px", fontWeight: "800", background: "#dbeafe", color: "#1d4ed8", padding: "2px 6px", borderRadius: "4px" }}>PICKUP</span>
                  </div>
                  <div style={{ fontSize: "13px", fontWeight: "800", color: "#1e293b" }}>Store Pickups</div>
                  <div style={{ fontSize: "18px", fontWeight: "900", color: "#0f172a", marginTop: "4px" }}>
                    {channelBreakdown.STORE_PICKUP?.count || 0} <span style={{ fontSize: "12px", fontWeight: "600", color: "#64748b" }}>orders</span>
                  </div>
                  <div style={{ fontSize: "12px", color: "#64748b", marginTop: "2px", fontWeight: "600" }}>
                    {inrFormatter.format(channelBreakdown.STORE_PICKUP?.revenue || 0)}
                  </div>
                </div>
              </div>

              {/* 3. Reserve With Advance */}
              <div className="col-md">
                <div style={{ background: "#f8fafc", border: "1.5px solid #edf0f4", borderRadius: "12px", padding: "14px 16px", height: "100%" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "8px" }}>
                    <span style={{ fontSize: "20px" }}>🔖</span>
                    <span style={{ fontSize: "10px", fontWeight: "800", background: "#fef3c7", color: "#b45309", padding: "2px 6px", borderRadius: "4px" }}>HYBRID</span>
                  </div>
                  <div style={{ fontSize: "13px", fontWeight: "800", color: "#1e293b" }}>Advance Token</div>
                  <div style={{ fontSize: "18px", fontWeight: "900", color: "#0f172a", marginTop: "4px" }}>
                    {channelBreakdown.RESERVE_ADVANCE?.count || 0} <span style={{ fontSize: "12px", fontWeight: "600", color: "#64748b" }}>orders</span>
                  </div>
                  <div style={{ fontSize: "12px", color: "#64748b", marginTop: "2px", fontWeight: "600" }}>
                    {inrFormatter.format(channelBreakdown.RESERVE_ADVANCE?.revenue || 0)}
                  </div>
                </div>
              </div>

              {/* 4. Book Product */}
              <div className="col-md">
                <div style={{ background: "#f8fafc", border: "1.5px solid #edf0f4", borderRadius: "12px", padding: "14px 16px", height: "100%" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "8px" }}>
                    <span style={{ fontSize: "20px" }}>📌</span>
                    <span style={{ fontSize: "10px", fontWeight: "800", background: "#f3e8ff", color: "#7e22ce", padding: "2px 6px", borderRadius: "4px" }}>24H HOLD</span>
                  </div>
                  <div style={{ fontSize: "13px", fontWeight: "800", color: "#1e293b" }}>Hold Bookings</div>
                  <div style={{ fontSize: "18px", fontWeight: "900", color: "#0f172a", marginTop: "4px" }}>
                    {channelBreakdown.BOOKING?.count || 0} <span style={{ fontSize: "12px", fontWeight: "600", color: "#64748b" }}>holds</span>
                  </div>
                  <div style={{ fontSize: "12px", color: "#64748b", marginTop: "2px", fontWeight: "600" }}>
                    Pay at shop counter
                  </div>
                </div>
              </div>

              {/* 5. Direct Calls & Leads */}
              <div className="col-md">
                <div style={{ background: "#f8fafc", border: "1.5px solid #edf0f4", borderRadius: "12px", padding: "14px 16px", height: "100%" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "8px" }}>
                    <span style={{ fontSize: "20px" }}>📞</span>
                    <span style={{ fontSize: "10px", fontWeight: "800", background: "#ffedd5", color: "#c2410c", padding: "2px 6px", borderRadius: "4px" }}>DIRECT</span>
                  </div>
                  <div style={{ fontSize: "13px", fontWeight: "800", color: "#1e293b" }}>Call Inquiries</div>
                  <div style={{ fontSize: "18px", fontWeight: "900", color: "#0f172a", marginTop: "4px" }}>
                    {channelBreakdown.CALL_INQUIRY?.count || 0} <span style={{ fontSize: "12px", fontWeight: "600", color: "#64748b" }}>calls</span>
                  </div>
                  <div style={{ fontSize: "12px", color: "#64748b", marginTop: "2px", fontWeight: "600" }}>
                    Store Footfall: {channelBreakdown.STORE_VISIT?.count || 0}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Geographic Inflow (Where Customers Come From) */}
        <div className="col-lg-12">
          <div
            style={{
              background: "#ffffff",
              border: "1px solid #e0e4ea",
              borderRadius: "14px",
              padding: "22px 24px",
              boxShadow: "0 4px 14px rgba(16,20,28,0.03)"
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "18px", flexWrap: "wrap", gap: "10px" }}>
              <div>
                <span style={{ fontSize: "11px", fontWeight: "800", color: "var(--primary)", textTransform: "uppercase", letterSpacing: "0.8px" }}>
                  GEOGRAPHIC DEMAND &amp; INFLOW
                </span>
                <h3 style={{ fontSize: "18px", fontWeight: "900", color: "var(--dark)", margin: "2px 0 0" }}>
                  Where Your Customers &amp; Orders Come From 📍
                </h3>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <span style={{ fontSize: "12.5px", fontWeight: "700", color: "#334155" }}>
                  📍 Store Location: <strong>{customerGeographicInflow.vendorCity || "Local Hub"}</strong>
                </span>
              </div>
            </div>

            {/* Local vs Outstation Split */}
            <div style={{ background: "#f8fafc", border: "1px solid #edf0f4", borderRadius: "12px", padding: "16px 20px", marginBottom: "18px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", fontWeight: "800", marginBottom: "8px" }}>
                <span style={{ color: "#16a34a", display: "flex", alignItems: "center", gap: "5px" }}>
                  🏠 Local Store Neighborhood Radius: {customerGeographicInflow.localPercentage ?? 100}% ({customerGeographicInflow.localCount || 0} orders/leads)
                </span>
                <span style={{ color: "#2563eb", display: "flex", alignItems: "center", gap: "5px" }}>
                  ✈️ Outstation &amp; Regional: {customerGeographicInflow.outstationPercentage ?? 0}% ({customerGeographicInflow.outstationCount || 0} orders)
                </span>
              </div>
              <div style={{ height: "10px", width: "100%", background: "#e2e8f0", borderRadius: "99px", overflow: "hidden", display: "flex" }}>
                <div style={{ width: `${customerGeographicInflow.localPercentage ?? 100}%`, background: "#16a34a", transition: "width 0.4s ease" }} />
                <div style={{ width: `${customerGeographicInflow.outstationPercentage ?? 0}%`, background: "#2563eb", transition: "width 0.4s ease" }} />
              </div>
            </div>

            <div className="row g-4">
              {/* Top Cities List */}
              <div className="col-md-7">
                <h4 style={{ fontSize: "14px", fontWeight: "800", color: "#334155", marginBottom: "12px" }}>
                  Top Customer Inflow Cities &amp; Regions
                </h4>
                {customerGeographicInflow.topCities && customerGeographicInflow.topCities.length > 0 ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                    {customerGeographicInflow.topCities.map((item, idx) => (
                      <div key={idx} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: "13px", padding: "8px 12px", background: "#fbfcfd", border: "1px solid #f1f5f9", borderRadius: "8px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <span style={{ width: "22px", height: "22px", borderRadius: "50%", background: "#eff6ff", color: "#2563eb", fontSize: "11px", fontWeight: "800", display: "grid", placeItems: "center" }}>
                            {idx + 1}
                          </span>
                          <strong style={{ color: "#0f172a" }}>{item.city}</strong>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
                          <span style={{ color: "#64748b", fontWeight: "600" }}>{item.count} orders / leads</span>
                          <strong style={{ color: "#0f172a" }}>{inrFormatter.format(item.revenue)}</strong>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ padding: "20px", textAlign: "center", color: "#64748b", fontSize: "13px", background: "#f8fafc", borderRadius: "8px" }}>
                    Customer location trends will populate automatically as shoppers browse and order from your store.
                  </div>
                )}
              </div>

              {/* Top Pincodes & Demand Zones */}
              <div className="col-md-5">
                <h4 style={{ fontSize: "14px", fontWeight: "800", color: "#334155", marginBottom: "12px" }}>
                  Top Delivery Pincodes
                </h4>
                {customerGeographicInflow.topPincodes && customerGeographicInflow.topPincodes.length > 0 ? (
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "8px" }}>
                    {customerGeographicInflow.topPincodes.map((pin, idx) => (
                      <div key={idx} style={{ padding: "10px 12px", background: "#f8fafc", border: "1px solid #edf0f4", borderRadius: "8px" }}>
                        <div style={{ fontSize: "13.5px", fontWeight: "900", color: "#0f172a" }}>📮 {pin.pincode}</div>
                        <div style={{ fontSize: "11.5px", color: "#64748b", marginTop: "2px" }}>{pin.city} &bull; {pin.count} orders</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ padding: "20px", textAlign: "center", color: "#64748b", fontSize: "13px", background: "#f8fafc", borderRadius: "8px" }}>
                    Top delivery postal zones will appear here.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ═══════════════════════ 5. TOP PRODUCTS & ORDER ACTIVITY ═══════════════════════ */}
      <div className="row g-4">
        {/* Left: Top Products Table & Best Seller */}
        <div className="col-lg-7">
          <div
            style={{
              background: "#ffffff",
              border: "1px solid #e0e4ea",
              borderRadius: "14px",
              padding: "22px 24px",
              boxShadow: "0 4px 14px rgba(16,20,28,0.03)",
              height: "100%",
              display: "flex",
              flexDirection: "column"
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
              <div>
                <span style={{ fontSize: "11px", fontWeight: "800", color: "var(--primary)", textTransform: "uppercase", letterSpacing: "0.8px" }}>
                  CATALOGUE PERFORMANCE
                </span>
                <h3 style={{ fontSize: "18px", fontWeight: "900", color: "var(--dark)", margin: "2px 0 0" }}>
                  Top Products
                </h3>
              </div>
              <Link to="/vendor/products" style={{ fontSize: "12.5px", fontWeight: "800", color: "var(--primary)", textDecoration: "none" }}>
                View All ({productCount}) &rarr;
              </Link>
            </div>

            {/* Best Seller Banner if exists */}
            {bestSeller && (
              <div
                style={{
                  background: "#fffbfa",
                  border: "1.5px solid #faedea",
                  borderRadius: "10px",
                  padding: "12px 16px",
                  display: "flex",
                  alignItems: "center",
                  gap: "14px",
                  marginBottom: "14px"
                }}
              >
                <div style={{ width: "48px", height: "48px", borderRadius: "8px", background: "#f0f2f5", overflow: "hidden", flexShrink: 0 }}>
                  {bestSeller.mainImage ? (
                    <img src={bestSeller.mainImage} alt={bestSeller.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  ) : (
                    "📦"
                  )}
                </div>
                <div style={{ flex: 1 }}>
                  <span style={{ fontSize: "10px", fontWeight: "900", color: "var(--primary)", textTransform: "uppercase" }}>
                    ★ #1 BEST SELLER
                  </span>
                  <div style={{ fontSize: "14.5px", fontWeight: "900", color: "var(--dark)" }}>{bestSeller.name}</div>
                  <div style={{ fontSize: "12px", color: "var(--text-muted)", marginTop: "2px" }}>
                    {bestSeller.ordersCount} orders &middot; {inrFormatter.format(bestSeller.revenue)} revenue &middot; Stock:{" "}
                    <strong style={{ color: bestSeller.productStock <= 5 ? "#dc2626" : "var(--dark)" }}>{bestSeller.productStock}</strong>
                  </div>
                </div>
              </div>
            )}

            {/* Products Table */}
            {topProducts.length > 0 ? (
              <div style={{ flex: 1, overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                  <thead>
                    <tr style={{ borderBottom: "1.5px solid #edf0f4", color: "var(--text-muted)", fontSize: "11px", fontWeight: "800", textTransform: "uppercase" }}>
                      <th style={{ padding: "8px 0", textAlign: "left" }}>PRODUCT</th>
                      <th style={{ padding: "8px 10px", textAlign: "center" }}>ORDERS</th>
                      <th style={{ padding: "8px 10px", textAlign: "center" }}>SOLD</th>
                      <th style={{ padding: "8px 0", textAlign: "right" }}>REVENUE</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topProducts.slice(0, 4).map((p) => (
                      <tr key={p._id} style={{ borderBottom: "1px solid #edf0f4" }}>
                        <td style={{ padding: "10px 0" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                            <div style={{ width: "32px", height: "32px", borderRadius: "6px", background: "#f0f2f5", overflow: "hidden", flexShrink: 0 }}>
                              {p.mainImage ? <img src={p.mainImage} alt={p.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : "📦"}
                            </div>
                            <div>
                              <strong style={{ fontSize: "13px", color: "#111315", display: "block" }}>{p.name}</strong>
                              <span style={{ fontSize: "11.5px", color: "var(--text-muted)" }}>{inrFormatter.format(p.price)}</span>
                            </div>
                          </div>
                        </td>
                        <td style={{ padding: "10px", textAlign: "center", fontWeight: "700" }}>{p.ordersCount}</td>
                        <td style={{ padding: "10px", textAlign: "center", fontWeight: "700" }}>{p.unitsSold || p.ordersCount}</td>
                        <td style={{ padding: "10px 0", textAlign: "right", fontWeight: "900", color: "#111315" }}>
                          {inrFormatter.format(p.revenue)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ textAlign: "center", padding: "30px", color: "var(--text-muted)", fontSize: "13px" }}>
                No product sales yet. Add products and share your store link to start receiving orders.
              </div>
            )}
          </div>
        </div>

        {/* Right: Order Activity & Recent Orders */}
        <div className="col-lg-5">
          <div
            style={{
              background: "#ffffff",
              border: "1px solid #e0e4ea",
              borderRadius: "14px",
              padding: "22px 24px",
              boxShadow: "0 4px 14px rgba(16,20,28,0.03)",
              height: "100%",
              display: "flex",
              flexDirection: "column"
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px" }}>
              <div>
                <span style={{ fontSize: "11px", fontWeight: "800", color: "var(--primary)", textTransform: "uppercase", letterSpacing: "0.8px" }}>
                  FULFILLMENT PIPELINE
                </span>
                <h3 style={{ fontSize: "18px", fontWeight: "900", color: "var(--dark)", margin: "2px 0 0" }}>
                  Order Activity
                </h3>
              </div>
              <Link to="/vendor/orders" style={{ fontSize: "12.5px", fontWeight: "800", color: "var(--primary)", textDecoration: "none" }}>
                View All &rarr;
              </Link>
            </div>

            {/* Status Breakdown Pills */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: "6px", marginBottom: "16px", textAlign: "center" }}>
              <div style={{ background: "#f8fafc", padding: "8px 4px", borderRadius: "8px", border: "1px solid #edf0f4" }}>
                <span style={{ fontSize: "10px", color: "var(--text-muted)", fontWeight: "800", textTransform: "uppercase" }}>NEW</span>
                <div style={{ fontSize: "16px", fontWeight: "900", color: "#111315", marginTop: "2px" }}>{orderActivity.new || 0}</div>
              </div>
              <div style={{ background: "#f8fafc", padding: "8px 4px", borderRadius: "8px", border: "1px solid #edf0f4" }}>
                <span style={{ fontSize: "10px", color: "var(--text-muted)", fontWeight: "800", textTransform: "uppercase" }}>PROC</span>
                <div style={{ fontSize: "16px", fontWeight: "900", color: "var(--primary)", marginTop: "2px" }}>{orderActivity.processing || 0}</div>
              </div>
              <div style={{ background: "#f8fafc", padding: "8px 4px", borderRadius: "8px", border: "1px solid #edf0f4" }}>
                <span style={{ fontSize: "10px", color: "var(--text-muted)", fontWeight: "800", textTransform: "uppercase" }}>SHIP</span>
                <div style={{ fontSize: "16px", fontWeight: "900", color: "#2563eb", marginTop: "2px" }}>{orderActivity.shipped || 0}</div>
              </div>
              <div style={{ background: "#f8fafc", padding: "8px 4px", borderRadius: "8px", border: "1px solid #edf0f4" }}>
                <span style={{ fontSize: "10px", color: "var(--text-muted)", fontWeight: "800", textTransform: "uppercase" }}>DELIV</span>
                <div style={{ fontSize: "16px", fontWeight: "900", color: "#16a34a", marginTop: "2px" }}>{orderActivity.delivered || 0}</div>
              </div>
              <div style={{ background: "#f8fafc", padding: "8px 4px", borderRadius: "8px", border: "1px solid #edf0f4" }}>
                <span style={{ fontSize: "10px", color: "var(--text-muted)", fontWeight: "800", textTransform: "uppercase" }}>RET</span>
                <div style={{ fontSize: "16px", fontWeight: "900", color: orderActivity.returns > 0 ? "#dc2626" : "var(--dark)", marginTop: "2px" }}>{orderActivity.returns || 0}</div>
              </div>
            </div>

            {/* Recent Orders List */}
            {recentOrders.length > 0 ? (
              <div style={{ display: "flex", flexDirection: "column", gap: "8px", flex: 1 }}>
                {recentOrders.slice(0, 4).map((ord) => (
                  <div
                    key={ord._id}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "8px 0",
                      borderBottom: "1px solid #edf0f4"
                    }}
                  >
                    <div>
                      <strong style={{ fontSize: "13px", color: "#111315", display: "block" }}>{ord.customer}</strong>
                      <span style={{ fontSize: "11.5px", color: "var(--text-muted)" }}>
                        {ord.items?.length || 1} item &middot; {new Date(ord.createdAt).toLocaleDateString("en-IN", { month: "short", day: "numeric" })}
                      </span>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <strong style={{ fontSize: "13.5px", color: "#111315", display: "block" }}>{inrFormatter.format(ord.vendorOrderValue)}</strong>
                      <span
                        style={{
                          fontSize: "10.5px",
                          fontWeight: "800",
                          color: ord.orderStatus === "Delivered" ? "#16a34a" : "var(--primary)"
                        }}
                      >
                        ● {ord.orderStatus}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: "center", padding: "24px", color: "var(--text-muted)", fontSize: "13px", flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                No active orders right now. Orders will appear here as soon as customers buy.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ═══════════════════════ 6. NEEDS YOUR ATTENTION ═══════════════════════ */}
      {attentionItems.length > 0 && (
        <div
          style={{
            background: "#ffffff",
            border: "1px solid #e0e4ea",
            borderRadius: "14px",
            padding: "20px 24px",
            boxShadow: "0 4px 14px rgba(16,20,28,0.03)"
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "14px" }}>
            <AlertTriangleIcon size={18} color="#d97706" />
            <h3 style={{ fontSize: "16px", fontWeight: "900", color: "var(--dark)", margin: 0 }}>
              Needs Your Attention
            </h3>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            {attentionItems.map((item, idx) => (
              <div
                key={idx}
                style={{
                  background: item.type === "danger" ? "#fef2f2" : item.type === "warning" ? "#fffbeb" : "#eff6ff",
                  border: `1px solid ${item.type === "danger" ? "#fecaca" : item.type === "warning" ? "#fde68a" : "#bfdbfe"}`,
                  borderRadius: "10px",
                  padding: "12px 18px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: "12px"
                }}
              >
                <div>
                  <strong style={{ fontSize: "13.5px", color: item.type === "danger" ? "#991b1b" : item.type === "warning" ? "#92400e" : "#1e40af" }}>
                    {item.title}
                  </strong>
                  <span style={{ fontSize: "12.5px", opacity: 0.85, marginLeft: "6px" }}>— {item.desc}</span>
                </div>
                <Link
                  to={item.to}
                  style={{
                    fontSize: "12.5px",
                    fontWeight: "800",
                    color: item.type === "danger" ? "#991b1b" : item.type === "warning" ? "#92400e" : "#1e40af",
                    textDecoration: "underline"
                  }}
                >
                  Take Action &rarr;
                </Link>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ═══════════════════════ 7. QUICK ACTION CENTER (4 ACTIONS ONLY) ═══════════════════════ */}
      <div
        style={{
          background: "#ffffff",
          border: "1px solid #e0e4ea",
          borderRadius: "14px",
          padding: "20px 24px",
          boxShadow: "0 4px 14px rgba(16,20,28,0.03)"
        }}
      >
        <div style={{ marginBottom: "14px" }}>
          <span style={{ fontSize: "11px", fontWeight: "800", color: "var(--primary)", textTransform: "uppercase", letterSpacing: "0.8px" }}>
            SHORTCUTS
          </span>
          <h3 style={{ fontSize: "16px", fontWeight: "900", color: "var(--dark)", margin: "2px 0 0" }}>
            Quick Actions
          </h3>
        </div>

        <div className="row g-3">
          <div className="col-md-3 col-6">
            <Link
              to="/vendor/products/new"
              style={{
                background: "#fbfaf8",
                border: "1px solid #edf0f4",
                borderRadius: "10px",
                padding: "14px",
                textDecoration: "none",
                display: "flex",
                alignItems: "center",
                gap: "12px",
                transition: "all 0.2s ease"
              }}
              className="card-brand-hover"
            >
              <div style={{ width: "36px", height: "36px", borderRadius: "8px", background: "#fff5f4", color: "var(--primary)", display: "grid", placeItems: "center" }}>
                <PlusIcon size={18} />
              </div>
              <div>
                <strong style={{ fontSize: "13px", color: "#111315", display: "block" }}>Add Product</strong>
                <span style={{ fontSize: "11.5px", color: "var(--text-muted)" }}>List new item</span>
              </div>
            </Link>
          </div>

          <div className="col-md-3 col-6">
            <Link
              to="/vendor/store"
              style={{
                background: "#fbfaf8",
                border: "1px solid #edf0f4",
                borderRadius: "10px",
                padding: "14px",
                textDecoration: "none",
                display: "flex",
                alignItems: "center",
                gap: "12px",
                transition: "all 0.2s ease"
              }}
              className="card-brand-hover"
            >
              <div style={{ width: "36px", height: "36px", borderRadius: "8px", background: "#f0fdf4", color: "#16a34a", display: "grid", placeItems: "center" }}>
                <StoreIcon size={18} />
              </div>
              <div>
                <strong style={{ fontSize: "13px", color: "#111315", display: "block" }}>Manage Store</strong>
                <span style={{ fontSize: "11.5px", color: "var(--text-muted)" }}>Edit branding</span>
              </div>
            </Link>
          </div>

          <div className="col-md-3 col-6">
            <Link
              to="/vendor/orders"
              style={{
                background: "#fbfaf8",
                border: "1px solid #edf0f4",
                borderRadius: "10px",
                padding: "14px",
                textDecoration: "none",
                display: "flex",
                alignItems: "center",
                gap: "12px",
                transition: "all 0.2s ease"
              }}
              className="card-brand-hover"
            >
              <div style={{ width: "36px", height: "36px", borderRadius: "8px", background: "#eff6ff", color: "#2563eb", display: "grid", placeItems: "center" }}>
                <ShoppingBagIcon size={18} />
              </div>
              <div>
                <strong style={{ fontSize: "13px", color: "#111315", display: "block" }}>View Orders</strong>
                <span style={{ fontSize: "11.5px", color: "var(--text-muted)" }}>Track fulfillment</span>
              </div>
            </Link>
          </div>

          <div className="col-md-3 col-6">
            <Link
              to="/vendor/subscription"
              style={{
                background: "#fbfaf8",
                border: "1px solid #edf0f4",
                borderRadius: "10px",
                padding: "14px",
                textDecoration: "none",
                display: "flex",
                alignItems: "center",
                gap: "12px",
                transition: "all 0.2s ease"
              }}
              className="card-brand-hover"
            >
              <div style={{ width: "36px", height: "36px", borderRadius: "8px", background: "#faf5ff", color: "#7c3aed", display: "grid", placeItems: "center" }}>
                <CreditCardIcon size={18} />
              </div>
              <div>
                <strong style={{ fontSize: "13px", color: "#111315", display: "block" }}>Subscription</strong>
                <span style={{ fontSize: "11.5px", color: "var(--text-muted)" }}>{currentPlan.name} plan</span>
              </div>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
